import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  MapPin, RefreshCw, Users, Navigation, Clock, 
  Phone, User, Activity, Maximize2, Minimize2,
  Signal, SignalLow, MapPinned, Target, Crosshair,
  Wifi, WifiOff, Radio
} from 'lucide-react';
import axios from 'axios';
import io from 'socket.io-client';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EnhancedLiveLocationMap = ({ onClose }) => {
  const [locations, setLocations] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Initialize WebSocket connection
  useEffect(() => {
    const newSocket = io(API_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    newSocket.on('connect', () => {
      console.log('WebSocket connected for location tracking');
      setConnected(true);
      newSocket.emit('join_location_room', {});
    });

    newSocket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    });

    newSocket.on('location_update', (data) => {
      if (data.locations) {
        setLocations(data.locations);
        setLastUpdate(new Date());
      }
    });

    newSocket.on('staff_moved', (data) => {
      setLocations(prev => {
        const updated = [...prev];
        const index = updated.findIndex(l => l.staff_id === data.staff_id);
        if (index >= 0) {
          updated[index] = { ...updated[index], ...data };
        } else {
          updated.push(data);
        }
        return updated;
      });
      setLastUpdate(new Date());
    });

    setSocket(newSocket);

    return () => {
      newSocket.emit('leave_location_room', {});
      newSocket.close();
    };
  }, []);

  // Fetch locations via REST API as fallback
  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('field_staff_token');
      const response = await axios.get(
        `${API_URL}/api/field-staff/admin/staff-locations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const locData = response.data?.locations || response.data || [];
      setLocations(locData);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    
    // Auto-refresh every 15 seconds if WebSocket not connected
    let interval;
    if (autoRefresh && !connected) {
      interval = setInterval(fetchLocations, 15000);
    }
    return () => interval && clearInterval(interval);
  }, [fetchLocations, autoRefresh, connected]);

  const getSignalStrength = (accuracy) => {
    if (!accuracy) return { level: 'unknown', color: 'gray' };
    if (accuracy <= 10) return { level: 'excellent', color: 'green' };
    if (accuracy <= 50) return { level: 'good', color: 'blue' };
    if (accuracy <= 100) return { level: 'fair', color: 'yellow' };
    return { level: 'poor', color: 'red' };
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const handleStaffClick = (staff) => {
    setSelectedStaff(staff);
  };

  const handleCall = (phone) => {
    if (phone) window.location.href = `tel:${phone}`;
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  // Calculate map bounds and positions
  const getStaffPositions = () => {
    if (locations.length === 0) return [];
    
    // Normalize positions for visualization
    const lats = locations.map(l => l.latitude);
    const lngs = locations.map(l => l.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    const latRange = maxLat - minLat || 0.01;
    const lngRange = maxLng - minLng || 0.01;
    
    return locations.map(loc => ({
      ...loc,
      x: ((loc.longitude - minLng) / lngRange) * 80 + 10,
      y: ((maxLat - loc.latitude) / latRange) * 80 + 10
    }));
  };

  const staffPositions = getStaffPositions();

  return (
    <div 
      className={`bg-white rounded-xl shadow-xl overflow-hidden ${
        isFullscreen ? 'fixed inset-4 z-50' : ''
      }`}
      data-testid="live-location-map"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Live Location Tracking</h2>
              <div className="flex items-center gap-2 text-blue-100 text-sm">
                <span>{locations.length} staff members</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  {connected ? (
                    <><Wifi className="w-3 h-3 text-green-300" /> Live</>
                  ) : (
                    <><WifiOff className="w-3 h-3 text-yellow-300" /> Polling</>
                  )}
                </span>
                {lastUpdate && (
                  <>
                    <span>•</span>
                    <span>Updated {getTimeSince(lastUpdate)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchLocations}
              className="text-white hover:bg-white/20"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-white hover:bg-white/20"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
                ✕
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex" style={{ height: isFullscreen ? 'calc(100vh - 180px)' : '500px' }}>
        {/* Map Area */}
        <div className="flex-1 relative bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
          {/* Grid lines */}
          <svg className="absolute inset-0 w-full h-full opacity-20">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>

          {/* Compass */}
          <div className="absolute top-4 left-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center">
            <Navigation className="w-6 h-6 text-blue-600" />
          </div>

          {/* Staff Markers */}
          {staffPositions.map((loc, idx) => {
            const signal = getSignalStrength(loc.accuracy);
            const isSelected = selectedStaff?.staff_id === loc.staff_id;
            
            return (
              <div
                key={loc.staff_id}
                className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 cursor-pointer ${
                  isSelected ? 'z-20 scale-125' : 'z-10 hover:scale-110'
                }`}
                style={{ left: `${loc.x}%`, top: `${loc.y}%` }}
                onClick={() => handleStaffClick(loc)}
              >
                {/* Pulse animation for recently updated */}
                {new Date() - new Date(loc.timestamp) < 60000 && (
                  <div className={`absolute inset-0 w-12 h-12 -m-2 rounded-full bg-${signal.color}-400 animate-ping opacity-30`}></div>
                )}
                
                {/* Marker */}
                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg border-2 ${
                  isSelected ? 'border-white ring-4 ring-blue-400' : 'border-white/50'
                } ${
                  signal.color === 'green' ? 'bg-green-500' :
                  signal.color === 'blue' ? 'bg-blue-500' :
                  signal.color === 'yellow' ? 'bg-yellow-500' :
                  signal.color === 'red' ? 'bg-red-500' : 'bg-gray-400'
                }`}>
                  {loc.staff_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                
                {/* Name label */}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 bg-white rounded-lg shadow-md text-xs font-medium whitespace-nowrap ${
                  isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}>
                  {loc.staff_name}
                </div>
              </div>
            );
          })}

          {/* Empty state */}
          {locations.length === 0 && !loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8">
                <MapPinned className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-600">No Active Locations</h3>
                <p className="text-sm text-gray-500">Staff members with location sharing will appear here</p>
              </div>
            </div>
          )}

          {/* Loading state */}
          {loading && locations.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50">
              <Activity className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          )}

          {/* Selected Staff Card */}
          {selectedStaff && (
            <div className="absolute bottom-4 left-4 right-4 bg-white rounded-xl shadow-xl p-4 z-30 border border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl ${
                    getSignalStrength(selectedStaff.accuracy).color === 'green' ? 'bg-green-500' :
                    getSignalStrength(selectedStaff.accuracy).color === 'blue' ? 'bg-blue-500' :
                    'bg-gray-400'
                  }`}>
                    {selectedStaff.staff_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{selectedStaff.staff_name}</h4>
                    <p className="text-sm text-gray-500">{selectedStaff.position || 'Field Staff'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={getSignalStrength(selectedStaff.accuracy).level === 'excellent' ? 'default' : 'secondary'}>
                        <Signal className="w-3 h-3 mr-1" />
                        {getSignalStrength(selectedStaff.accuracy).level}
                      </Badge>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getTimeSince(selectedStaff.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openGoogleMaps(selectedStaff.latitude, selectedStaff.longitude)}
                  >
                    <Target className="w-4 h-4 mr-1" />
                    Maps
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleCall(selectedStaff.phone)}
                  >
                    <Phone className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setSelectedStaff(null)}
                  >
                    ✕
                  </Button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{selectedStaff.latitude?.toFixed(6)}, {selectedStaff.longitude?.toFixed(6)}</span>
                </div>
                {selectedStaff.accuracy && (
                  <div className="flex items-center gap-1">
                    <Crosshair className="w-4 h-4 text-gray-400" />
                    <span>±{Math.round(selectedStaff.accuracy)}m accuracy</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Staff List Sidebar */}
        <div className="w-80 border-l bg-gray-50 overflow-y-auto">
          <div className="p-3 bg-white border-b sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Staff Members</span>
              <Badge variant="outline" className="flex items-center gap-1">
                <Radio className="w-3 h-3" />
                {locations.length}
              </Badge>
            </div>
          </div>
          
          <div className="p-2 space-y-2">
            {locations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="font-medium">No staff locations</p>
                <p className="text-xs">Waiting for location updates...</p>
              </div>
            ) : (
              locations.map((loc) => {
                const signal = getSignalStrength(loc.accuracy);
                const isSelected = selectedStaff?.staff_id === loc.staff_id;
                
                return (
                  <div
                    key={loc.staff_id}
                    onClick={() => handleStaffClick(loc)}
                    className={`p-3 bg-white rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      isSelected ? 'border-blue-500 shadow-md ring-2 ring-blue-100' : 'border-gray-200'
                    }`}
                    data-testid={`staff-location-${loc.staff_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                        signal.color === 'green' ? 'bg-green-500' :
                        signal.color === 'blue' ? 'bg-blue-500' :
                        signal.color === 'yellow' ? 'bg-yellow-500' :
                        signal.color === 'red' ? 'bg-red-500' : 'bg-gray-400'
                      }`}>
                        {loc.staff_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{loc.staff_name}</h4>
                        <p className="text-xs text-gray-500">{loc.position || 'Field Staff'}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {signal.level === 'excellent' ? (
                            <Signal className="w-4 h-4 text-green-500" />
                          ) : signal.level === 'good' ? (
                            <Signal className="w-4 h-4 text-blue-500" />
                          ) : (
                            <SignalLow className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{getTimeSince(loc.timestamp)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border-t px-4 py-2">
        <div className="flex items-center justify-center gap-6 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div> Excellent Signal
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div> Good Signal
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div> Fair Signal
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div> Poor Signal
          </span>
        </div>
      </div>
    </div>
  );
};

export default EnhancedLiveLocationMap;
